'use client'

import dynamic from 'next/dynamic'
import Image from 'next/image'

import { JobCardItem, TinyJobItem } from '@types'

import clsx from 'clsx'

import BenefitsLabels from '@components/benefits-labels'
import useDateTime from '@hooks/client/useDateTime'
import useI18n from '@hooks/client/useI18n'
import { getSalaryFromJob, jobUrlBuilder } from '@lib/helpers/client'

import companyPlaceholderIcon from '@images/searchresults/company.png'
import locationIcon from '@images/searchresults/icon_location-pin.svg'

import AIEstimatedLabel from './ai-estimated-label'

import styles from './job-card.module.scss'

import JobCardLoggedInFeatures from './job-card-logged-in-features'
import Link from 'next/link'
import useLocale from '@hooks/client/useLocale'
import isSalaryVisible from '@lib/helpers/cross-env/is-salary-visible'

const JobCardDescription = dynamic(() => import('./job-card-description'))

type JobCardProps = {
  job: TinyJobItem | JobCardItem
  dataCy?: string
  onClick?: () => void
  variant?: 'full' | 'compact'
  isAuthenticated?: boolean
}

export default function JobCard({
  job,
  dataCy,
  onClick,
  variant = 'full',
  isAuthenticated,
}: JobCardProps): JSX.Element {
  const dateTime = useDateTime()
  const { currency } = useI18n()
  const country = useLocale()
  const salary = getSalaryFromJob(job, currency)
  const jobCardId = `job-card-${job.id}`
  const source = 'source' in job ? job.source + '' : ''
  const showSalary = isSalaryVisible(job.companyName, source, job.salary?.isEstimated ?? false)
  if (!job) return <div>No detailed found for job</div>

  const classNames = clsx(styles['search-result'], {
    [styles.compact]: variant === 'compact',
    [styles.highlight]: job.premium,
  })
  return (
    <div className={classNames} data-pw={dataCy} id={jobCardId}>
      <Link href={jobUrlBuilder(job, country)} onClick={onClick} data-pw={`${dataCy}-link`} prefetch={false}>
        <div className={styles['job-details']}>
          <div className={styles.header}>
            <div className={styles['company-pic']}>
              <Image
                src={job.companyJobLogoURL || job.featuredImageUrl || companyPlaceholderIcon}
                alt="Company"
                width={40}
                height={40}
              />
            </div>
            <div className={styles.title}>
              <h2>{job.title}</h2>
            </div>
            {showSalary && (
              <div className={styles.salary}>
                <span style={{ marginRight: 10 }}>{salary}</span>
                {salary !== 'Competitive' && job?.salary?.isEstimated && <AIEstimatedLabel describedBy={jobCardId} />}
              </div>
            )}
            <div className={styles.company}>
              <p>{job.companyName}</p>
            </div>
          </div>
          {variant === 'full' && 'jobDescription' in job && <JobCardDescription description={job.jobDescription} />}
          {'benefits' in job && Number(job?.benefits?.length) > 0 && variant === 'full' && (
            <div style={{ margin: '20px 0' }}>
              <BenefitsLabels benefits={job.benefits!} />
            </div>
          )}
          <div className={styles.info}>
            <div className={styles.location}>
              <Image src={locationIcon} alt="Location" />
              <p>{job.address?.city}</p>
            </div>
            <div className={styles['date-posted']}>
              <p>{dateTime(job.publishDateTime || 0).fromNow()}</p>
            </div>
          </div>
        </div>
      </Link>
      {isAuthenticated && variant === 'full' && 'showBookmark' in job && (
        <JobCardLoggedInFeatures jobId={job.id} showBookmark={job.showBookmark} />
      )}
    </div>
  )
}
